import { Guide, Tour, GuideRanking } from '../types';
import { USE_BACKEND_API } from '../config/apiConfig';
import { apiService } from './apiService';
import { supabase } from './supabaseService';

class GuideService {
  private storageKey = 'maasai_sighttrails_guides';
  private readonly CACHE_KEY = 'guides_cache';
  private readonly CACHE_TIMESTAMP_KEY = 'guides_cache_timestamp';
  private readonly CACHE_DURATION = 4 * 60 * 60 * 1000; // 4 hour cache
  private cache: Guide[] = [];
  private cacheTimestamp: number = 0;
  private pendingRequest: Promise<Guide[]> | null = null;

  constructor() {
    this.loadCacheFromStorage();
  }

  private loadCacheFromStorage(): void {
    try {
      const cachedData = localStorage.getItem(this.CACHE_KEY);
      const cachedTimestamp = localStorage.getItem(this.CACHE_TIMESTAMP_KEY);

      if (cachedData && cachedTimestamp) {
        const timestamp = parseInt(cachedTimestamp, 10);
        if (Date.now() - timestamp < this.CACHE_DURATION) {
          this.cache = JSON.parse(cachedData);
          this.cacheTimestamp = timestamp;
        }
      }
    } catch (error) {
      console.error('Error loading cache from localStorage:', error);
    }
  }

  private saveCacheToStorage(guides: Guide[]): void {
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(guides));
      localStorage.setItem(this.CACHE_TIMESTAMP_KEY, Date.now().toString());
      localStorage.setItem(this.storageKey, JSON.stringify(guides));
    } catch (error) {
      console.error('Error saving cache to localStorage:', error);
    }
  }

  private isCacheValid(): boolean {
    return this.cache.length > 0 && (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION;
  }

  private clearCache(): void {
    this.cache = [];
    this.cacheTimestamp = 0;
    localStorage.removeItem(this.CACHE_KEY);
    localStorage.removeItem(this.CACHE_TIMESTAMP_KEY);
  }

  // Convert database guide to frontend format
  private convertDbGuideToFrontend(dbGuide: any): Guide {
    return {
      id: dbGuide.id,
      firstName: dbGuide.firstName || dbGuide.first_name || '',
      lastName: dbGuide.lastName || dbGuide.last_name || '',
      email: dbGuide.email || '',
      phone: dbGuide.phone || '',
      bio: dbGuide.bio || '',
      experience: dbGuide.experience || '',
      languages: Array.isArray(dbGuide.languages) ? dbGuide.languages : [],
      specialties: Array.isArray(dbGuide.specialties) ? dbGuide.specialties : (Array.isArray(dbGuide.specialities) ? dbGuide.specialities : []),
      rating: dbGuide.rating ? Number(dbGuide.rating) : 5.0,
      avatar: dbGuide.avatar || '',
      certifications: Array.isArray(dbGuide.certifications) ? dbGuide.certifications : [],
      location: dbGuide.location || '',
      rankingId: dbGuide.rankingId || dbGuide.ranking_id || undefined,
      availability: dbGuide.availability !== false,
      emergencyContact: dbGuide.emergencyContact || dbGuide.emergency_contact || {
        name: '',
        phone: '',
        relationship: ''
      },
      active: dbGuide.active !== false,
      createdAt: dbGuide.createdAt || dbGuide.created_at || new Date().toISOString(),
      updatedAt: dbGuide.updatedAt || dbGuide.updated_at || new Date().toISOString()
    };
  }

  async getGuides(forceRefresh: boolean = false): Promise<Guide[]> {
    if (!forceRefresh && this.isCacheValid()) {
      return [...this.cache];
    }

    if (this.pendingRequest) {
      return this.pendingRequest;
    }

    this.pendingRequest = (async () => {
      try {
        let guides: Guide[] = [];

        if (USE_BACKEND_API) {
          const response = await apiService.getAllGuides();
          const rawGuides = response || [];
          guides = rawGuides.map((g: any) => this.convertDbGuideToFrontend(g));
        } else {
          // Fallback extraction from tours if backend API is not available
          const { tourService } = await import('./tourService');
          const tours = await tourService.getAllTours(forceRefresh);
          const guideMap = new Map<string, Guide>();
          (tours as Tour[]).forEach((tour: Tour) => {
            if (tour.guides && tour.guides.length > 0) {
              tour.guides.forEach((guide: Guide) => {
                if (guide?.id) {
                  guideMap.set(guide.id, this.convertDbGuideToFrontend(guide));
                }
              });
            }
          });
          guides = Array.from(guideMap.values());
        }

        if (guides.length > 0) {
          this.cache = guides;
          this.cacheTimestamp = Date.now();
          this.saveCacheToStorage(guides);
        }

        return guides;
      } catch (error) {
        console.error('Error fetching guides:', error);
        return this.cache.length > 0 ? [...this.cache] : this.getFromLocalStorage();
      } finally {
        this.pendingRequest = null;
      }
    })();

    return this.pendingRequest;
  }

  async getActiveGuides(forceRefresh: boolean = false): Promise<Guide[]> {
    const guides = await this.getGuides(forceRefresh);
    return guides.filter(guide => guide.active);
  }

  async getGuideById(id: string): Promise<Guide | null> {
    // 1. Check local cache first
    const cached = this.cache.find(g => g.id === id);
    if (cached) {
      // Revalidate in background but return cache immediately
      this.getGuides(true).catch(() => { });
      return cached;
    }

    try {
      if (USE_BACKEND_API) {
        const rawGuide = await apiService.getGuideById(id);
        if (!rawGuide) return null;
        
        const guide = this.convertDbGuideToFrontend(rawGuide);
        
        // Update cache if possible
        const idx = this.cache.findIndex(g => g.id === id);
        if (idx !== -1) {
          this.cache[idx] = guide;
        } else {
          this.cache.push(guide);
        }
        this.saveCacheToStorage(this.cache);
        
        return guide;
      }
      
      const guides = await this.getGuides();
      return guides.find(g => g.id === id) || null;
    } catch (error) {
      console.error('Error fetching guide by ID:', error);
      return null;
    }
  }

  async createGuide(guideData: Omit<Guide, 'id' | 'createdAt' | 'updatedAt'>): Promise<Guide> {
    try {
      if (USE_BACKEND_API) {
        const newGuide = await apiService.createGuide(guideData);
        this.clearCache();
        return newGuide;
      }
      throw new Error('Create guide only supported with backend API');
    } catch (error) {
      console.error('Error creating guide:', error);
      throw error;
    }
  }

  async updateGuide(id: string, guideData: Partial<Guide>): Promise<Guide | null> {
    try {
      if (USE_BACKEND_API) {
        const updatedGuide = await apiService.updateGuide(id, guideData);
        this.clearCache();
        return updatedGuide;
      }

      console.log('📡 Updating guide in Supabase:', id);
      // Map to snake_case for Supabase
      const supabaseUpdate: any = {
        updated_at: new Date().toISOString()
      };

      if (guideData.firstName) supabaseUpdate.first_name = guideData.firstName;
      if (guideData.lastName) supabaseUpdate.last_name = guideData.lastName;
      if (guideData.rating !== undefined) supabaseUpdate.rating = guideData.rating;
      if (guideData.active !== undefined) supabaseUpdate.active = guideData.active;
      if (guideData.specialties) supabaseUpdate.specialties = guideData.specialties;
      if (guideData.languages) supabaseUpdate.languages = guideData.languages;

      const { data, error } = await supabase.from('guides').update(supabaseUpdate).eq('id', id).select().single();
      if (error) {
        if (error.code === 'PGRST116') {
          console.warn('⚠️ Guide table or record not found in Supabase. Skipping DB update.');
          return null;
        }
        throw error;
      }

      this.clearCache();
      return data;
    } catch (error) {
      console.error('Error updating guide:', error);
      throw error;
    }
  }

  async deleteGuide(id: string): Promise<boolean> {
    try {
      if (USE_BACKEND_API) {
        await apiService.deleteGuide(id);
        this.clearCache();
        return true;
      }
      throw new Error('Delete guide only supported with backend API');
    } catch (error) {
      console.error('Error deleting guide:', error);
      return false;
    }
  }

  async toggleGuideStatus(id: string): Promise<Guide | null> {
    const guide = await this.getGuideById(id);
    if (!guide) return null;
    return await this.updateGuide(id, { active: !guide.active });
  }

  // Get guides by specialty
  async getGuidesBySpecialty(specialty: string): Promise<Guide[]> {
    const guides = await this.getActiveGuides();
    return guides.filter(guide =>
      guide.specialties.some(s =>
        s.toLowerCase().includes(specialty.toLowerCase())
      )
    );
  }

  // Update guide rating based on their tours (Now handled by backend sync)
  async updateGuideRating(guideId: string): Promise<Guide | null> {
    return null;
  }

  // Update all guide ratings via backend sync
  async updateAllGuideRatings(): Promise<void> {
    try {
      if (USE_BACKEND_API) {
        await apiService.post('/guides/sync-ratings', {});
      }
    } catch (error) {
      // Silent error in background
    }
  }

  private getFromLocalStorage(): Guide[] {
    try {
      const guides = localStorage.getItem(this.storageKey);
      return guides ? JSON.parse(guides) : [];
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return [];
    }
  }

  // Guide Rankings
  async getGuideRankings(): Promise<GuideRanking[]> {
    try {
      const data = await apiService.get<GuideRanking[]>('/logistics/guide-rankings');
      return data || [];
    } catch (error) {
      console.error('Error fetching guide rankings:', error);
      return [];
    }
  }

  async getGuideRankingById(id: string): Promise<GuideRanking | null> {
    try {
      return await apiService.get<GuideRanking>(`/logistics/guide-rankings/${id}`);
    } catch (error) {
      console.error(`Error fetching guide ranking ${id}:`, error);
      return null;
    }
  }

  async createGuideRanking(data: Omit<GuideRanking, 'id'>): Promise<GuideRanking | null> {
    try {
      return await apiService.post<GuideRanking>('/logistics/guide-rankings', data);
    } catch (error) {
      console.error('Error creating guide ranking:', error);
      return null;
    }
  }

  async updateGuideRanking(id: string, data: Partial<GuideRanking>): Promise<GuideRanking | null> {
    try {
      return await apiService.put<GuideRanking>(`/logistics/guide-rankings/${id}`, data);
    } catch (error) {
      console.error(`Error updating guide ranking ${id}:`, error);
      return null;
    }
  }

  async deleteGuideRanking(id: string): Promise<boolean> {
    try {
      await apiService.delete(`/logistics/guide-rankings/${id}`);
      return true;
    } catch (error) {
      console.error(`Error deleting guide ranking ${id}:`, error);
      return false;
    }
  }
}

export const guideService = new GuideService();
export default guideService;
// cache bust
